import { useEffect, useId, useState, type ReactNode } from 'react';
import { REQUIREMENT_LABEL, ROLE_FIELDS, fieldDef } from '../../guidance/roleFieldRegistry';
import { jobRoleGateway } from '../../index';
import { formatBandRange, inputFromPayBandForm } from '../../payBand';
import type { FormIssue } from '../../roleForm';
import type { JobRoleClassification, JobRoleContent, OccupationDataset, OccupationSystem, PayBand } from '../../types';
import { EmploymentGuidance, Facts, ListBlock, QualificationsPanel, ResponsibilitiesPanel, TextBlock } from '../JobDescription';
import { COMPENSATION_SOURCE_LIMITATIONS, type PendingBand } from './CompensationStep';
import { STEP_LABEL, stepOfField, type StepKey } from './steps';
import { useWizard } from './WizardContext';

export interface ReviewStepProps {
  content: JobRoleContent;
  activationIssues: FormIssue[];
  /** What the server last stamped on the role (null for a role not yet saved). */
  savedClassification: JobRoleClassification;
  pendingBand: PendingBand | null;
  existingBand: PayBand | null | undefined;
  onEdit: (step: StepKey) => void;
  onJump: (key: string) => void;
  /** Ids the footer buttons point at with aria-describedby. */
  ids: { draft: string; activate: string };
}

function Section({ title, step, onEdit, children }: { title: string; step: StepKey; onEdit: (s: StepKey) => void; children: ReactNode }) {
  const id = useId();
  return (
    <section className="jr-review-section" aria-labelledby={id}>
      <div className="jr-review-section__head">
        <h4 id={id}>{title}</h4>
        <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => onEdit(step)}>
          Edit{' '}<span className="jr-sr-only">{title}</span>
        </button>
      </div>
      {children}
    </section>
  );
}

/** A recommended field is "empty" for the optional list when it holds nothing (an explicit "not yet classified" counts as an answer). */
function isEmpty(key: string, form: Record<string, string>): boolean {
  if (key === 'socCode') return !form.socCode?.trim() && form.socStatus !== 'NotYetClassified';
  return !form[key]?.trim();
}

/**
 * Step 6, Review. The role as a job description in four sections (each with an Edit button that returns to its step without losing anything), with
 * codes translated to names: the department (marked when it was created in this session), the SOC and O*NET code with its official title and dataset
 * version, or "Not yet classified" with the reason. Above it: what needs attention before the role can be activated (each item a link that goes to the
 * step and the field), and the recommended-but-optional items. Saving a draft and activating are explained as the different things they are.
 */
export function ReviewStep({ content, activationIssues, savedClassification, pendingBand, existingBand, onEdit, onJump, ids }: ReviewStepProps) {
  const w = useWizard();
  const f = w.form;
  const [datasets, setDatasets] = useState<OccupationDataset[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    jobRoleGateway.listOccupationDatasets(controller.signal).then((d) => { if (!controller.signal.aborted) setDatasets(d); }).catch(() => undefined);
    return () => controller.abort();
  }, []);

  const department = w.departments.find((d) => d.id === f.departmentId);
  const isNewDepartment = !!department && w.newDepartmentIds.includes(department.id);
  const recommended = ROLE_FIELDS.filter((d) => d.requirement === 'recommended' && isEmpty(d.key, f as unknown as Record<string, string>));

  const occupationLine = (system: OccupationSystem): ReactNode => {
    const code = system === 'Soc' ? f.socCode : f.onetCode;
    const status = system === 'Soc' ? f.socStatus : f.onetStatus;
    const reason = system === 'Soc' ? f.socNotYetClassifiedReason : f.onetNotYetClassifiedReason;
    const saved = system === 'Soc' ? savedClassification.socDatasetVersion : savedClassification.onetDatasetVersion;
    const info = w.occupation[system];
    if (status === 'NotYetClassified') return <>Not yet classified. Reason: {reason || 'none given'}</>;
    if (!code) return null;
    const version = saved ?? datasets.find((d) => d.system === system && d.isCurrent !== false)?.version ?? null;
    return (
      <>
        {code}{info ? ` ${info.title}` : ' (saved value, not in the current dataset)'}
        {version ? <span className="muted"> ({system === 'Soc' ? 'SOC' : 'O*NET'} list version {version})</span> : null}
      </>
    );
  };

  const blockedReason = activationIssues.length > 0
    ? `Activating is not available yet: ${activationIssues.length} ${activationIssues.length === 1 ? 'item needs' : 'items need'} attention. You can still save a draft.`
    : '';

  return (
    <div className="jr-step" data-testid="review-step">
      {pendingBand && (
        <p className="jr-notice" role="note" data-testid="wizard-review-band">
          An employer pay band ({formatBandRange(inputFromPayBandForm(pendingBand.values))}) will be saved with this role.
        </p>
      )}
      <p className="muted">Read the whole job description before you save it. A draft can be edited later; activating it publishes it as the current version.</p>

      <section className="jr-attention" aria-labelledby={`${ids.activate}-attention`}>
        <h4 id={`${ids.activate}-attention`}>Needs attention before activating</h4>
        {activationIssues.length > 0 ? (
          <div className="jr-notice" data-testid="needs-attention">
            <p>To activate this role you still need to fix these. You can still save it as a draft now.</p>
            <ul className="jr-plain-list">
              {activationIssues.map((i) => (
                <li key={`${i.key}-${i.message}`}>
                  <button type="button" className="link" onClick={() => onJump(i.key)}>
                    {i.message}{' '}<span className="jr-sr-only">Go to {STEP_LABEL[stepOfField(i.key)]}, {fieldDef(i.key).label}</span>
                  </button>
                  <span className="muted"> ({STEP_LABEL[stepOfField(i.key)]})</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="jr-ok" role="note"><span aria-hidden="true">{'✓'} </span>This role has what it needs to be activated.</p>
        )}
      </section>

      {recommended.length > 0 && (
        <section className="jr-attention" aria-labelledby={`${ids.draft}-recommended`}>
          <h4 id={`${ids.draft}-recommended`}>Recommended but optional</h4>
          <p className="jr-hint">None of these is needed to save or activate. Filling them in makes the role easier to find and compare.</p>
          <ul className="jr-plain-list" data-testid="recommended-list">
            {recommended.map((d) => (
              <li key={d.key}>
                <button type="button" className="link" onClick={() => onJump(d.key)}>
                  {d.label}{' '}<span className="jr-sr-only">({REQUIREMENT_LABEL[d.requirement]}). Go to {STEP_LABEL[stepOfField(d.key)]}</span>
                </button>
                <span className="muted"> ({STEP_LABEL[stepOfField(d.key)]})</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <article className="jr-jd jr-review" aria-label={`Job description for ${f.title || 'this role'}`}>
        <h3>{f.title || 'Untitled role'}</h3>

        <Section title="Role details" step="details" onEdit={onEdit}>
          <Facts
            items={[
              ['Internal code', f.roleCode],
              ['Department', department ? (
                <>
                  {department.name}{department.code ? <span className="muted"> (code {department.code})</span> : null}
                  {isNewDepartment && <span className="jr-badge jr-badge--new" data-testid="new-department-marker"> New department created in this session</span>}
                </>
              ) : null],
              ['Job family', f.jobFamily],
              ['Level', f.careerLevel],
              ['Management level', f.managementLevel],
              ['SOC code', occupationLine('Soc')],
              ['O*NET code', occupationLine('Onet')],
              ['Description', f.description],
            ]}
          />
          <ListBlock title="Alternate titles" items={content.alternateTitles} />
          <TextBlock title="Summary" text={content.summary} />
          <TextBlock title="Purpose" text={content.purpose} />
        </Section>

        <Section title="Responsibilities" step="responsibilities" onEdit={onEdit}>
          <ResponsibilitiesPanel content={content} hidePurpose />
        </Section>

        <Section title="Qualifications" step="qualifications" onEdit={onEdit}>
          <QualificationsPanel content={content} />
        </Section>

        <Section title="Compensation" step="compensation" onEdit={onEdit}>
          <EmploymentGuidance content={content} />
          <Facts
            items={[
              ['Employer pay band', pendingBand
                ? `${formatBandRange(inputFromPayBandForm(pendingBand.values))} (to be saved with this role)`
                : existingBand ? formatBandRange(existingBand) : 'Not set'],
            ]}
          />
          <p className="jr-hint" data-testid="review-compensation-limits">{COMPENSATION_SOURCE_LIMITATIONS}</p>
        </Section>
      </article>

      <div className="jr-review-actions" data-testid="review-actions">
        <p id={ids.draft}><strong>Save as draft:</strong> You can finish later; it is not available in Add Employee.</p>
        <p id={ids.activate}><strong>Activate:</strong> Makes the role available to assign to employees.{blockedReason ? ` ${blockedReason}` : ''}</p>
      </div>
    </div>
  );
}
